import { Coupon, Credit, Offer, SubscriptionPlan } from "../api/model";

import cookies from "../cookies";
import * as dialog from "../dialog";
import { BillingPeriod, CouponStatus, PlanGroup } from "../enums";

import { formatCurrency } from "./legacy";

interface GetPlanPriceParams {
    plan: SubscriptionPlan;
    seatCount: number;
    period: BillingPeriod;
    coupon?: Coupon;
    offer?: Offer;
    credit?: Credit;
    personalPlanTier?:number;
}
type GetPlanPriceReturnType = {
    totalPrice: number;
    unitPrice: number;
    creditUsed: number;
    totalPriceBeforeCredit: number;
};

const PRICE_DIVIDER = 100;
const MONTHS_IN_YEAR = 12;

type getCouponStatusParams = { coupon?: Coupon; period: BillingPeriod; };
export function getCouponStatus({ coupon, period }: getCouponStatusParams): CouponStatus {
    if (!coupon || !period) {
        return CouponStatus.NOT_FOUND;
    }

    if (coupon.redeem_by && Date.now() / 1000 > coupon.redeem_by) {
        return CouponStatus.EXPIRED;
    }

    if (coupon.max_redemptions && coupon.times_redeemed >= coupon.max_redemptions) {
        return CouponStatus.MAXED_OUT;
    }

    return CouponStatus.SUCCESS;
}

export function getDurationText(coupon: Coupon, period: BillingPeriod): string {
    switch (coupon.duration) {
        case "once":
            return `for the first ${period === "monthly" ? "month" : "year"}`;

        case "repeating":
            return `for ${coupon.duration_in_months} months`;

        case "forever":
            return "forever";

        default:
            return "";
    }
}

export function getDiscountNotice(coupon: Coupon, period: BillingPeriod): string {
    if (!coupon || period !== "monthly") {
        return "";
    }

    if (coupon.redeem_by && Date.now() / 1000 > coupon.redeem_by) {
        return "This coupon is expired";
    }

    if (coupon.max_redemptions && coupon.times_redeemed >= coupon.max_redemptions) {
        return "This coupon is maxed out";
    }

    const discountText = coupon.percent_off
        ? `${coupon.percent_off}% off`
        : `${formatCurrency(coupon.amount_off / 100)} off`;

    return `${discountText} ${getDurationText(coupon, period)}`;
}

export function formatPriceForUI(amount: number) {
    return amount / PRICE_DIVIDER;
}

export function getPlanPrice({
    plan,
    period,
    seatCount,
    coupon,
    offer,
    credit,
    personalPlanTier
}: GetPlanPriceParams): GetPlanPriceReturnType {
    let totalPrice = 0;
    let unitPrice = plan.price;
    let creditUsed = 0;
    let totalPriceBeforeCredit = 0;

    if (personalPlanTier) {
        const planTier = plan.tiers!.find(({ quantity }) => quantity === personalPlanTier)!;
        unitPrice = planTier.price;
    }

    if (getCouponStatus({ coupon, period }) === CouponStatus.SUCCESS) {
        if (coupon!.amount_off) {
            totalPrice -= coupon!.amount_off;
        } else {
            unitPrice = getDiscountedPrice(unitPrice, coupon!.percent_off);
        }
    }

    totalPrice += unitPrice * seatCount;

    if (period === BillingPeriod.ANNUALLY) {
        unitPrice /= MONTHS_IN_YEAR;
    }

    if (!coupon && offer) {
        if (offer.amount) {
            totalPrice -= offer.amount;
        } else if (offer.percentage) {
            totalPrice = getDiscountedPrice(totalPrice, offer.percentage);
            unitPrice = getDiscountedPrice(unitPrice, offer.percentage);
        }

        totalPriceBeforeCredit = totalPrice;
        if (credit) {
            creditUsed = credit.credit + credit.balance;
            totalPrice -= creditUsed;
            if (totalPrice < 0) {
                creditUsed += totalPrice;
            }
        }
    }

    totalPrice = Math.max(0, formatPriceForUI(totalPrice));
    unitPrice = Math.max(0, formatPriceForUI(unitPrice));
    creditUsed = Math.max(0, formatPriceForUI(creditUsed));
    totalPriceBeforeCredit = Math.max(0, formatPriceForUI(totalPriceBeforeCredit));

    return {
        totalPrice,
        unitPrice,
        creditUsed,
        totalPriceBeforeCredit
    };
}

export function getDiscountedPrice(price: number, percentage: number): number {
    return price - (price * percentage / PRICE_DIVIDER);
}

export function formatPriceForStripe(amount: number) {
    return Math.max(0, amount * PRICE_DIVIDER);
}

export function isOfferValid(offer: Offer) {
    return new Date(offer.validUntil).getTime() - new Date().getTime() > 0;
}

export function hasTeamPlan(planId: string) {
    // TODO: Revisit this function and make it use plan.group instead
    return planId.includes("team");
}

export function getActivePlan(plans: SubscriptionPlan[], group: PlanGroup, period: BillingPeriod): SubscriptionPlan {
    return plans.find(plan => plan.group === group && plan.period === period && plan.status === "active")!;
}

export function getPeriodText(billingPeriod: BillingPeriod) {
    return billingPeriod === BillingPeriod.MONTHLY ? "monthly" : "annually";
}

export function checkInvoiceError() {
    const invoiceError = cookies.get("invoiceError");
    if (!invoiceError) {
        return null;
    }

    dialog.create(JSON.parse(invoiceError));
    cookies.remove("invoiceError");
}
